// --- REWRITE START ---
import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'react-hot-toast';
import {
  UserIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  EyeIcon,
  StarIcon,
  CheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  HomeIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import logo from './images/FINAL-DESIGN-LOGO-01.png';
import {
  HeartIcon as HeartSolidIcon,
  StarIcon as StarSolidIcon,
} from '@heroicons/react/24/solid';
import axios from 'axios';
import AdminDashboard from './components/admin/AdminDashboard';
import ProfilePage from './pages/ProfilePage';
import './App.css';
import CircleAnime from './CircleAnime';
import Footer from './components/Footer';

import { API } from './config';
import Navigation from './components/Navigation';
import BlogPage from './pages/BlogPage';
import BlogPostDetail from './pages/BlogPostDetail';
import HomePage from './pages/HomePage';
import { AppProvider, useAppContext } from './context/AppContext';



// Redundant components removed. Using separate files for HomePage, ProductsPage, etc.


// Local HomePage definition removed.


// Helper: Display product info for a section product
// SectionProductCard removed






// Enhanced Checkout Page

// ProfilePage component moved to pages/ProfilePage.js

const LoginPage = () => {
  const { login } = useAppContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex items-center justify-center pt-20">
      {/* Background Video */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/videos/hero-bg.mp4" type="video/mp4" />
          <div className="absolute inset-0 bg-agro-green-dark" />
        </video>
        {/* Overlay gradient - Dark Green for better text prominence */}
        <div className="absolute inset-0 bg-gradient-to-b from-agro-green-dark/80 via-agro-green-dark/60 to-agro-green-dark/90" />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 mx-4 shadow-2xl"
      >
        <div className="text-center mb-10">
          <div className="inline-block p-4 rounded-2xl bg-agro-lime/10 mb-4">
            <UserIcon className="w-8 h-8 text-agro-lime" />
          </div>
          <h2 className="text-3xl font-serif text-white">Welcome Back</h2>
          <p className="mt-2 text-gray-300 font-sans">Sign in to your dashboard</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-agro-lime mb-2 ml-1">Email Address</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-agro-lime/50 focus:border-agro-lime outline-none transition-all text-white placeholder-gray-500"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-agro-lime mb-2 ml-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-agro-lime/50 focus:border-agro-lime outline-none transition-all text-white placeholder-gray-500"
              placeholder="Enter your password"
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-agro-lime text-agro-green-dark py-5 rounded-2xl font-bold hover:bg-white transition-all duration-300 shadow-xl shadow-agro-lime/10 uppercase tracking-widest text-sm mt-4"
          >
            Sign In
          </motion.button>
        </form>

        <p className="mt-10 text-center text-gray-400 font-sans">
          Don't have an account?{' '}
          <Link to="/register" className="font-bold text-agro-lime hover:text-white transition-colors uppercase tracking-widest text-xs">
            Create One
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

const RegisterPage = () => {
  const { register } = useAppContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: ''
  });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await register(formData);
    if (success) {
      navigate('/');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 mx-4"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-serif text-agro-black">Create Account</h2>
          <p className="mt-2 text-agro-green/60">Join the SR Agro Farms community</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <input
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-agro-green-light focus:border-agro-green outline-none transition-all"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-agro-green-light focus:border-agro-green outline-none transition-all"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-agro-green-light focus:border-agro-green outline-none transition-all"
              placeholder="Create a password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-agro-green-light focus:border-agro-green outline-none transition-all"
              placeholder="+91 98765 43210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <textarea
              name="address"
              rows="2"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-agro-green-light focus:border-agro-green outline-none transition-all"
              placeholder="Enter your address"
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-agro-green-dark text-white py-4 rounded-xl font-bold hover:bg-agro-black transition-all duration-300 shadow-xl shadow-agro-green-dark/10 uppercase tracking-widest text-sm"
          >
            Create Account
          </motion.button>
        </form>

        <p className="mt-8 text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-agro-green hover:text-agro-green-dark transition-colors uppercase tracking-widest text-xs">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

// Admin Wrapper Component
// Main Layout Component to handle conditional navigation
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/customize');

  useEffect(() => {
    // Only track non-admin routes for analytics
    if (!isAdminRoute) {
      axios.post(`${API}/track-visit`, { path: location.pathname })
        .catch(() => { /* Silent fail */ });
    }
  }, [location.pathname, isAdminRoute]);

  return (
    <>
      {!isAdminRoute && <Navigation />}
      {children}
    </>
  );
};



// Main App Component
const App = () => {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-center" />
        <MainLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </MainLayout>
      </BrowserRouter>
    </AppProvider>
  );
};

export default App;
